/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Combobox, useMantineTheme, Group, Text, rem } from '@mantine/core';
import { RiArrowRightSLine } from '@remixicon/react';

import { TextVariants } from '@fai/ui/components/typography/variants';

import { input } from './basic-header.css';

interface OptionItemProps {
  label: React.ReactNode;
  value: string;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  disabled?: boolean;
  isActive?: boolean;
  isVisible?: boolean;
  showChevron?: boolean;
  onClick?: VoidFunction;
}

export default function OptionItem({
  label,
  value,
  leftSlot,
  rightSlot,
  disabled,
  isActive,
  isVisible,
  showChevron,
  onClick,
}: OptionItemProps) {
  const theme = useMantineTheme();

  if (isVisible === false) return null;

  return (
    <Combobox.Option
      active={isActive}
      bg={isActive ? theme.pf.color.bg.cloudblue.default.base : undefined}
      classNames={{ option: input }}
      disabled={disabled}
      h={rem(40)}
      px={theme.pf.layout['2xs']}
      py={rem(9.5)}
      style={{ display: 'flex', alignItems: 'center', lineHeight: '1' }}
      value={value}
      onClick={onClick}
    >
      <Group justify="space-between" w="100%">
        <Group gap={theme.pf.layout['3xs']} wrap="nowrap">
          {leftSlot}

          <Text
            c={theme.pf.color.fg.neutral.strong}
            variant={TextVariants.BodyMd}
            lineClamp={1}
            style={{ flexShrink: 1 }}
          >
            {label}
          </Text>

          {showChevron && <RiArrowRightSLine size={16} />}
        </Group>

        {rightSlot}
      </Group>
    </Combobox.Option>
  );
}
